import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Invoice } from '../../types';
import Input from './Input';
import Select from './Select';

interface CurrencyOption {
  value: string;
  label: string;
}

interface InvoiceFinancialSectionProps {
  register: UseFormRegister<Invoice>;
  errors: FieldErrors<Invoice>;
  isDebitNote: boolean;
  selectedCurrency: string | undefined;
  currencyOptions: CurrencyOption[];
}

const InvoiceFinancialSection: React.FC<InvoiceFinancialSectionProps> = ({
  register,
  errors,
  isDebitNote,
  selectedCurrency,
  currencyOptions,
}) => {
  return (
    <div className="border-t pt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Details</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Currency"
          {...register('currency', { required: 'Currency is required' })}
          options={currencyOptions}
          error={errors.currency?.message}
        />
        <Input
          label="Subtotal"
          type="number"
          step="0.01"
          {...register('subtotal', {
            required: 'Subtotal is required',
            min: { value: 0, message: 'Subtotal cannot be negative' }
          })}
          error={errors.subtotal?.message}
          placeholder="0.00"
        />
      </div>

      {!isDebitNote && selectedCurrency === 'USD' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <Input
            label="Exchange Rate (USD to EGP)"
            type="number"
            step="0.01"
            {...register('exchangeRate', {
              required: selectedCurrency === 'USD' && !isDebitNote ? 'Exchange rate is required for USD' : false,
              min: { value: 0.01, message: 'Exchange rate must be greater than 0' }
            })}
            error={errors.exchangeRate?.message}
            placeholder="e.g., 50.00"
          />
          <div></div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
        {!isDebitNote && (
          <Input
            label="Tax (Auto-calculated 3%)"
            type="number"
            step="0.01"
            {...register('tax')}
            disabled
          />
        )}
        <Input
          label="Deduction"
          type="number"
          step="0.01"
          {...register('deduction', { min: { value: 0, message: 'Cannot be negative' } })}
          error={errors.deduction?.message}
          placeholder="0.00"
        />
        <Input
          label="Net After Tax & Deduction"
          type="number"
          step="0.01"
          {...register('netAfterTaxAndDeduction')}
          disabled
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
        <Input
          label="Total"
          type="number"
          step="0.01"
          {...register('total')}
          disabled
        />
        {!isDebitNote && (
          <Input
            label="Performance Bond"
            type="number"
            step="0.01"
            {...register('performanceBond', { min: { value: 0, message: 'Cannot be negative' } })}
            error={errors.performanceBond?.message}
            placeholder="0.00"
          />
        )}
        <Input
          label="Paid"
          type="number"
          step="0.01"
          {...register('paid', { min: { value: 0, message: 'Cannot be negative' } })}
          error={errors.paid?.message}
          placeholder="0.00"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
        <Input
          label="Net Remaining"
          type="number"
          step="0.01"
          {...register('netRemaining')}
          disabled
        />
        <div></div>
      </div>
    </div>
  );
};

export default InvoiceFinancialSection;
